import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import ErrorIcon from '@mui/icons-material/Error';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import SyncIcon from '@mui/icons-material/Sync';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Tooltip,
  Typography,
} from '@mui/material';
import { type Theme, useTheme } from '@mui/material/styles';
import type React from 'react';
import { useEffect, useState } from 'react';
import { usePricing } from '../../../contexts/PricingContext';
import { useSnackbar } from '../../../contexts/SnackbarContext';
import { useTeamLimits } from '../../../hooks/useTeamLimits';
import { apiService } from '../../../services/api';
import type { Tender } from '../../../types/tender';
import { EstimatedCostChip } from '../../common/EstimatedCostChip';
import type { LoadingStates, QuickSettings } from '../types';
import {
  calculateResetCooldown,
  extractProgressFromStatus,
  formatGenerationStatusText,
  getLoadingKey,
  isServiceProcessingStatus,
} from '../utils';

export interface DraftActionCardProps {
  tender: Tender;
  quickSettings: QuickSettings;
  userDocsCount: number;
  sectionsCount: number;
  loadingStates: LoadingStates;
  stepCompletion: Record<number, boolean>;
  onRegenerate: (docType: string) => void;
  onDownload: (gsURI: string, fileTypeIdentifier: string, defaultFileName: string) => void;
  onReset: () => void;
  onUpdateStatus: (docType: string, status: string) => void;
  onNavigateToDownload: () => void;
  onQuickSettingsChange: (newSettings: Partial<QuickSettings>) => void;
}

const DraftActionCard: React.FC<DraftActionCardProps> = ({
  tender,
  quickSettings,
  userDocsCount: _userDocsCount,
  sectionsCount,
  loadingStates,
  stepCompletion,
  onRegenerate,
  onDownload: _onDownload,
  onReset,
  onUpdateStatus,
  onNavigateToDownload,
  onQuickSettingsChange,
}) => {
  const theme = useTheme();
  const { pricingSettings } = usePricing();
  const { showSnackbar } = useSnackbar();
  const [resetCooldown, setResetCooldown] = useState<number>(0);
  const [errorReported, setErrorReported] = useState(false);
  const [errorDismissed, setErrorDismissed] = useState(false);
  const [lastErrorStatus, setLastErrorStatus] = useState('');
  const [confirmRegenOpen, setConfirmRegenOpen] = useState(false);

  const status = tender.draftResponse?.status || '';
  const statusLower = status.toLowerCase();
  const isError =
    (statusLower.includes('error') || statusLower.includes('failed')) &&
    !statusLower.includes('complete') &&
    !statusLower.includes('closed');

  const [maxProgress, setMaxProgress] = useState(0);
  useEffect(() => {
    const currentProgress = extractProgressFromStatus(status, tender.draftResponse?.progress) ?? 0;
    const activeProcessing = isServiceProcessingStatus(status);

    if (!activeProcessing) {
      setMaxProgress(0);
    } else {
      setMaxProgress((prev) => Math.max(prev, currentProgress));
    }
  }, [status, tender.draftResponse?.progress]);

  useEffect(() => {
    if (status !== lastErrorStatus) {
      setLastErrorStatus(status);
      if (!isError) {
        setErrorDismissed(false);
        setErrorReported(false);
      }
    }
  }, [status, isError, lastErrorStatus]);

  const handleReportError = async () => {
    try {
      await apiService.reportError({
        tenderId: tender.id || 'unknown',
        error: status,
        component: 'Draft Generation',
      });
      setErrorReported(true);
      setErrorDismissed(true);
      showSnackbar('Error reported. Thank you.', 'success');
      onUpdateStatus('response', 'Error Report Complete');
    } catch (_e) {
      showSnackbar('Failed to report error.', 'error');
    }
  };

  const loadingKey = getLoadingKey(tender.id, 'regenerate', 'draft_generation');
  const isLocalLoading = !!loadingStates[loadingKey];

  const getButtonState = () => {
    const status = tender.draftResponse?.status || '';
    const isProcessing = isLocalLoading || isServiceProcessingStatus(status);
    const exists = !!tender.draftFileLink;

    if (isProcessing) {
      return { text: '', showSpinner: true };
    }
    if (!exists) {
      return { text: 'Generate Draft', showSpinner: false };
    }
    return { text: 'Regenerate', showSpinner: false };
  };

  const buttonState = getButtonState();

  useEffect(() => {
    const updateCooldown = () => {
      setResetCooldown(calculateResetCooldown(tender.draftResponse, 75));
    };

    updateCooldown();
    const interval = setInterval(updateCooldown, 60000);
    return () => clearInterval(interval);
  }, [tender.draftResponse]);
  const getRegenButtonStyle = (theme: Theme, enabled: boolean) => ({
    backgroundColor: enabled ? theme.palette.greyContrast : theme.palette.greyLight,
    color: enabled ? theme.palette.greyLight : theme.palette.greyContrast,
    border: enabled ? 'none' : `1px solid ${theme.palette.greyContrast}`,
    fontSize: '1rem',
    fontWeight: 600,
    opacity: enabled ? 1 : 0.7,
    boxShadow: theme.shadows[2],
    height: '36px',
    minHeight: '36px',
    padding: theme.spacing(0.5, 2),
    '&:hover': enabled
      ? {
          backgroundColor: theme.palette.bidlogiq?.brick || '#b73f2a',
          color: '#fff',
          boxShadow: theme.shadows[4],
        }
      : {
          backgroundColor: theme.palette.greyLight,
          color: theme.palette.text.primary,
          boxShadow: theme.shadows[4],
        },
    '&.Mui-disabled': {
      backgroundColor: theme.palette.greyLight,
      color: theme.palette.greyContrast,
      border: `1px solid ${theme.palette.bidlogiq?.orange}`,
    },
    width: '100%',
    mb: 1,
  });

  const numCollections =
    tender.prioritizedCollections === undefined ? 5 : tender.prioritizedCollections.length;

  const preFlightChecks = [
    {
      label: 'Capability analysis complete',
      passed: !!tender.analysisFileLink,
      failureMessage: 'Please generate the capability analysis first.',
    },
    {
      label: 'Project Team defined',
      passed: !!tender.teamMembers && tender.teamMembers.length > 0,
      failureMessage: 'Please assign at least one team member to the Project Team.',
    },
    {
      label:
        numCollections > 0
          ? `${numCollections} Knowledge collection(s) active`
          : 'No knowledge collections selected',
      passed: numCollections > 0,
      failureMessage: 'Please select at least one knowledge collection in the documents step.',
    },
    {
      label: 'Proposal structure configured',
      passed:
        !!stepCompletion[7] ||
        (tender.customProposalStructure && tender.customProposalStructure.length > 0) ||
        (tender.hybridExtractedStructure && tender.hybridExtractedStructure.length > 0) ||
        false,
      failureMessage: 'Please review and confirm the draft structure in the Structure step.',
    },
  ];

  const isProcessing = buttonState.showSpinner;

  const { checkLimit } = useTeamLimits();
  const usedCount = tender.draftCount || 0;
  const { remaining, isLimitReached, isUnlimited } = checkLimit('drafts', usedCount);

  const getRegenerateButtonText = () => {
    if (isProcessing) {
      return formatGenerationStatusText(tender.draftResponse?.status, isLocalLoading);
    }
    const baseText = tender.draftFileLink ? 'Regenerate Draft' : 'Generate Draft';
    if (isLimitReached) {
      return `${baseText} (Limit Reached)`;
    }
    if (isUnlimited) {
      return baseText;
    }
    return `${baseText} (${remaining} remaining)`;
  };

  const hasSections =
    tender.draftResponse?.status === 'Sections Generated' ||
    tender.draftResponse?.status?.toLowerCase() === 'complete' ||
    !!tender.draftFileLink ||
    sectionsCount > 0;
  const structureAdherence = quickSettings?.structureAdherence || 'strict';

  return (
    <Box sx={{ width: '100%' }}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontSize: '1rem' }}>
        Generate a comprehensive first draft of your proposal based on the tender documents and your
        content library.
      </Typography>
      {hasSections && (
        <Alert severity="warning" sx={{ mb: 2, '& .MuiAlert-message': { fontSize: '1rem' } }}>
          Generating a new draft will replace all headings and text in the Review & Edit Sections
          step.
          <Box sx={{ mt: 0.5 }}>
            <Button
              variant="text"
              onClick={onNavigateToDownload}
              sx={{
                fontWeight: 'bold',
                textDecoration: 'underline',
                cursor: 'pointer',
                padding: 0,
                minWidth: 'auto',
                verticalAlign: 'baseline',
                color: 'inherit',
                fontSize: 'inherit',
                textTransform: 'none',
                '&:hover': {
                  textDecoration: 'none',
                  backgroundColor: 'transparent',
                },
              }}
            >
              Download a DOCX of the current version
            </Button>{' '}
            if you would like save it.
          </Box>
        </Alert>
      )}
      {isError && !errorDismissed && (
        <Box
          sx={{
            mb: 2,
            p: 1,
            px: 2,
            borderRadius: 1,
            bgcolor: (theme) => theme.palette.error.light,
            color: (theme) => theme.palette.error.contrastText,
            border: (theme) => `1px solid ${theme.palette.error.main}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, overflow: 'hidden' }}>
            <ErrorIcon fontSize="medium" />
            <Typography variant="body1" sx={{ fontWeight: 500 }} noWrap>
              <span style={{ fontWeight: 'bold' }}>Generation Failed:</span> {status} - Please
              report to admin.
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
            <Button
              variant="outlined"
              size="small"
              color="inherit"
              onClick={handleReportError}
              disabled={errorReported}
              sx={{
                whiteSpace: 'nowrap',
                bgcolor: 'white',
                color: 'error.main',
                borderColor: 'error.main',
                py: 0.5,
                px: 2,
                minHeight: '32px',
                fontSize: '1rem',
                fontWeight: 'bold',
                '&:hover': { bgcolor: 'grey.100' },
              }}
            >
              {errorReported ? 'Report Sent' : 'Notify Admin'}
            </Button>
            <IconButton
              size="small"
              onClick={() => {
                setErrorDismissed(true);
                onUpdateStatus('response', 'Error Closed');
              }}
              sx={{ color: 'inherit', p: 0.5 }}
            >
              {' '}
              <CloseIcon fontSize="small" />
            </IconButton>{' '}
          </Box>
        </Box>
      )}
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 8 }} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <FormControl fullWidth size="small">
              <InputLabel id="structure-adherence-label" sx={{ fontSize: '0.8rem' }}>
                Structure Adherence
              </InputLabel>
              <Select
                labelId="structure-adherence-label"
                value={structureAdherence}
                label="Structure Adherence"
                onChange={(e) =>
                  onQuickSettingsChange({
                    structureAdherence: e.target.value as 'strict' | 'fully_flexible',
                  })
                }
                sx={{
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderWidth: 2,
                    borderColor: theme.palette.bidlogiq.brick,
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.bidlogiq.brick,
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.bidlogiq.brick,
                  },
                }}
              >
                <MenuItem value="strict">Strictly adhere to your defined structure</MenuItem>
                {/* <MenuItem value="flexible_headings">Allow Extra Sub-Headings</MenuItem> */}
                <MenuItem value="fully_flexible">
                  Let the AI update, add, and remove headings
                </MenuItem>
              </Select>
            </FormControl>
            <Tooltip
              title={
                <Typography sx={{ fontSize: '1rem' }}>
                  Controls how strictly the AI follows your defined proposal structure. 'Strict'
                  forces exact adherence but adds subsections. 'Fully Flexible' allows the AI to
                  restructure, as needed, to best answer the tender.
                </Typography>
              }
              arrow
            >
              <InfoOutlinedIcon color="action" />
            </Tooltip>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ flexGrow: 1 }}>
              <Button
                data-testid="regenerate-response-button"
                onClick={() => {
                  setConfirmRegenOpen(true);
                }}
                disabled={
                  buttonState.showSpinner ||
                  (!tender.analysisFileLink &&
                    !tender.draftFileLink &&
                    !tender.draftResponse?.status?.includes('Complete')) ||
                  !!isLimitReached
                }
                sx={getRegenButtonStyle(
                  theme,
                  !buttonState.showSpinner &&
                    (!!tender.analysisFileLink ||
                      !!tender.draftFileLink ||
                      !!tender.draftResponse?.status?.includes('Complete')) &&
                    !isLimitReached
                )}
              >
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: '1fr auto 1fr',
                    width: '100%',
                    alignItems: 'center',
                  }}
                >
                  <Box /> {/* Left spacer for centering */}
                  <Box
                    sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}
                  >
                    {buttonState.showSpinner ? (
                      <CircularProgress
                        size={16}
                        variant="indeterminate"
                        sx={{ color: theme.palette.bidlogiq.orange }}
                      />
                    ) : (
                      <SyncIcon />
                    )}
                    <Box>{getRegenerateButtonText()}</Box>
                  </Box>
                  <Box sx={{ textAlign: 'right', minWidth: '35px' }}>
                    {buttonState.showSpinner &&
                    extractProgressFromStatus(status, tender.draftResponse?.progress) !== undefined
                      ? `${maxProgress}%`
                      : ''}
                  </Box>
                </Box>
              </Button>
            </Box>
            {!isLimitReached && (
              <Box>
                <Tooltip
                  title="Exact billing depends on the length and complexity of the tender documents and provided context."
                  slotProps={{ tooltip: { sx: { fontSize: '0.875rem', p: 1 } } }}
                >
                  <EstimatedCostChip
                    estimate={
                      pricingSettings?.estimates?.draft_response || 'Est. $200.00 – $700.00'
                    }
                  />
                </Tooltip>
              </Box>
            )}
          </Box>

          {tender.draftResponse?.status && (
            <Box
              sx={{
                mt: 1,
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                justifyContent: 'space-between',
              }}
            >
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ flexGrow: 1, fontSize: '0.875rem' }}
              >
                {' '}
                Generating a draft can take up to 75 minutes. Press the reset button if it is stuck.
              </Typography>
              <Button
                size="small"
                variant="outlined"
                color="error"
                onClick={onReset}
                disabled={resetCooldown > 0}
                sx={{
                  borderColor: 'text.error',
                  color: 'text.error',
                  whiteSpace: 'nowrap',
                }}
              >
                {resetCooldown > 0 ? `Reset (${resetCooldown}m)` : 'Reset'}
              </Button>
            </Box>
          )}
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <Box
            sx={{
              pl: { md: 2 },
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1, fontSize: '1rem' }}>
              Checks (optional)
            </Typography>
            {preFlightChecks.map((check) => (
              <Box key={check.label} sx={{ display: 'flex', flexDirection: 'column', mb: 0.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {check.passed ? (
                    <CheckCircleIcon sx={{ fontSize: 20, color: 'success.main' }} />
                  ) : (
                    <ErrorIcon sx={{ fontSize: 20, color: 'warning.main' }} />
                  )}
                  <Typography
                    variant="body1"
                    color={check.passed ? 'text.primary' : 'text.secondary'}
                    sx={{ fontSize: '1rem' }}
                  >
                    {check.label}
                  </Typography>
                </Box>
                {!check.passed && (
                  <Typography
                    variant="body2"
                    sx={{
                      ml: 4,
                      fontSize: '0.875rem',
                      color:
                        theme.palette.mode === 'dark' ? theme.palette.bidlogiq.camel : 'error.main',
                    }}
                  >
                    {check.failureMessage}
                  </Typography>
                )}
              </Box>
            ))}
          </Box>
        </Grid>
      </Grid>

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmRegenOpen}
        onClose={() => setConfirmRegenOpen(false)}
        aria-labelledby="confirm-regen-dialog-title"
        aria-describedby="confirm-regen-dialog-description"
      >
        <DialogTitle id="confirm-regen-dialog-title">
          Are you sure you want to generate a new draft?
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="confirm-regen-dialog-description">
            Generating a new draft will replace all headings and text in the Review & Edit Sections
            step.
            <br />
            <br />
            Download a DOCX of the current version if you would like save it.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmRegenOpen(false)} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={() => {
              setConfirmRegenOpen(false);
              onRegenerate('response');
            }}
            color="error"
            variant="contained"
            autoFocus
          >
            Yes, Generate Draft
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DraftActionCard;
